/**
 * Copyright schukai GmbH and contributors 2023. All Rights Reserved.
 * Node module: @schukai/monster
 * This file is licensed under the AGPLv3 License.
 * License text available at https://www.gnu.org/licenses/agpl-3.0.en.html
 */

import { Queue } from "./queue.mjs";
import { internalSymbol } from "../constants.mjs";
import { validateObject } from "./validate.mjs";

export { UniqueQueue };

/**
 * An UniqueQueue is a queue that contains items only once.
 *
 * @license AGPLv3
 * @since 1.4.0
 * @copyright schukai GmbH
 * @memberOf Monster.Types
 * @summary A queue for unique values
 */
class UniqueQueue extends Queue {
	/**
	 *
	 */
	constructor() {
		super();
		this[internalSymbol] = {
			unique: new WeakSet(),
		};
	}

	/**
	 * Add a new element to the end of the queue.
	 *
	 * @param {object} value
	 * @returns {Queue}
	 * @throws {TypeError} value is not a object
	 */
	add(value) {
		validateObject(value);

		if (!this[internalSymbol].unique.has(value)) {
			this[internalSymbol].unique.add(value);
			super.add(value);
		}

		return this;
	}

	/**
	 * remove all entries
	 *
	 * @returns {Queue}
	 */
	clear() {
		super.clear();
		this[internalSymbol].unique = new WeakSet();
		return this;
	}

	/**
	 * Remove the element at the front of the queue
	 * If the queue is empty, return undefined.
	 *
	 * @return {object}
	 */
	poll() {
		if (this.isEmpty()) {
			return undefined;
		}
		const value = this.data.shift();
		this[internalSymbol].unique.delete(value);
		return value;
	}
}
